<div
    class="graph-viewport"
    [class.is-readonly]="readOnly"
    [class.is-move]="moveMode"
    [class.is-panning]="panning"
    [class.is-space]="space">
  <!-- The move/up listeners are registered outside the Angular zone in
       ngAfterViewInit; binding them here would run change detection over the
       whole application once per frame of a drag. -->
  <div
      class="graph-surface"
      #surface
      [style.background-position]="panX + 'px ' + panY + 'px'"
      [style.background-size]="gridSize"
      (pointerenter)="pointerInside = true"
      (pointerleave)="pointerInside = false"
      (pointerdown)="onCanvasPointerDown($event)">
    <div
        class="graph-canvas"
        [class.is-panning]="panning"
        [style.transform]="'translate(' + panX + 'px, ' + panY + 'px) scale(' + scale + ')'">

      <svg class="graph-edges" width="100%" height="100%" aria-hidden="true">
        <defs>
          <marker id="wf-arrow" markerWidth="7" markerHeight="7" refX="8" refY="5"
                  viewBox="0 0 10 10" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" />
          </marker>
          <marker id="wf-arrow-on" markerWidth="7" markerHeight="7" refX="8" refY="5"
                  viewBox="0 0 10 10" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" />
          </marker>
        </defs>

        <g *ngFor="let edge of canvasEdges; trackBy: trackEdge"
           class="graph-edge"
           [class.is-selected]="selectedTransitionIndex === edge.index">
          <path class="edge-hit" [attr.d]="edge.path"
                (pointerdown)="onEdgePointerDown($event, edge.index)" />
          <path class="edge-line" [attr.d]="edge.path"
                [attr.marker-end]="selectedTransitionIndex === edge.index
                  ? 'url(#wf-arrow-on)' : 'url(#wf-arrow)'" />
        </g>

        <path class="link-preview" *ngIf="link"
              [attr.d]="'M ' + link.x1 + ' ' + link.y1 + ' L ' + link.x2 + ' ' + link.y2" />
      </svg>

      <article
          *ngFor="let item of canvasNodes; trackBy: trackNode"
          class="graph-node"
          [class.is-selected]="selectedNodeKey === item.node.key"
          [class.has-error]="errorNodeKeys.has(item.node.key)"
          [class.is-dragging]="draggingKey === item.node.key"
          [class.is-source]="link && link.fromNodeKey === item.node.key"
          [class.is-target]="hoverNodeKey === item.node.key && !hoverInvalid"
          [class.is-target-invalid]="hoverNodeKey === item.node.key && hoverInvalid"
          [class.is-expanded]="isNodeExpanded(item.node.key)"
          [style.left.px]="item.x"
          [style.top.px]="item.y"
          [style.width.px]="nodeWidth"
          [style.height.px]="item.height"
          (pointerdown)="onNodePointerDown($event, item)">
        <span *ngIf="link" class="node-entry" aria-hidden="true"></span>
        <div class="node-heading">
          <span class="node-dot" [style.background]="item.node.color || '#8a909c'"></span>
          <span class="node-name">{{ item.node.label }}</span>
          <span class="node-error" *ngIf="errorNodeKeys.has(item.node.key)" aria-label="Has an error">!</span>
        </div>
        <div class="node-fields-summary" *ngIf="item.node.fields.length; else noFields">
          <span class="node-fields">
            {{ item.node.fields.length }} field{{ item.node.fields.length === 1 ? '' : 's' }}
          </span>
          <button
              type="button"
              class="node-fields-toggle"
              [attr.aria-label]="isNodeExpanded(item.node.key) ? 'Hide fields' : 'Show fields'"
              [attr.aria-expanded]="isNodeExpanded(item.node.key)"
              (pointerdown)="onNodeContentPointerDown($event, item)"
              (click)="toggleNodeFields($event, item.node.key)">
            <br-icon>{{ isNodeExpanded(item.node.key) ? 'expand_less' : 'expand_more' }}</br-icon>
          </button>
        </div>
        <ng-template #noFields>
          <span class="node-fields is-empty">0 fields</span>
        </ng-template>

        <div
            class="node-field-list"
            *ngIf="isNodeExpanded(item.node.key)"
            (pointerdown)="onNodeContentPointerDown($event, item)">
          <div class="node-field-row" *ngFor="let field of item.node.fields">
            <span class="node-field-name">{{ field.label }}</span>
            <span class="node-field-meta">
              {{ fieldMeta(field) }}<span
                  *ngIf="field.isRequired"
                  class="br-required-mark"
                  aria-label="Required">*</span>
            </span>
          </div>
        </div>

        <button
            *ngIf="!readOnly"
            type="button"
            class="node-connector"
            aria-label="Drag to connect this node"
            (pointerdown)="onConnectorPointerDown($event, item)">
          <span aria-hidden="true">+</span>
        </button>
      </article>

      <ng-container *ngIf="selectedEdgeHandles as handles">
        <button
            *ngIf="!readOnly"
            type="button"
            class="edge-endpoint edge-endpoint--from"
            [style.left.px]="handles.x1"
            [style.top.px]="handles.y1"
            aria-label="Move transition start"
            title="Drag to change the start node"
            (pointerdown)="onTransitionEndpointPointerDown(
              $event, selectedTransitionIndex, 'from')">
        </button>
        <button
            *ngIf="!readOnly"
            type="button"
            class="edge-endpoint edge-endpoint--to"
            [style.left.px]="handles.x2"
            [style.top.px]="handles.y2"
            aria-label="Move transition end"
            title="Drag to change the end node"
            (pointerdown)="onTransitionEndpointPointerDown(
              $event, selectedTransitionIndex, 'to')">
        </button>
      </ng-container>

    </div>
  </div>

  <div class="canvas-controls" *ngIf="showControls">
    <button
        type="button"
        class="move-tool"
        [class.is-active]="moveMode"
        [attr.aria-pressed]="moveMode"
        aria-label="Move canvas"
        title="Move canvas"
        (click)="toggleMoveMode()">
      <br-icon>open_with</br-icon>
    </button>
    <div class="zoom-controls">
      <button type="button" aria-label="Zoom out" title="Zoom out"
              [disabled]="!canZoomOut" (click)="zoomOut()">−</button>
      <button type="button" class="zoom-fit" title="Fit the graph on screen"
              [disabled]="interactionActive" (click)="zoomToFit()">{{ zoomPercent }}%</button>
      <button type="button" aria-label="Zoom in" title="Zoom in"
              [disabled]="!canZoomIn" (click)="zoomIn()">+</button>
    </div>
  </div>
</div>
